import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { navCategories } from '@/mocks/categories';

export default function SiteHeader() {
  const navigate = useNavigate();
  const location = useLocation();
  const [keyword, setKeyword] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname, location.search]);

  const submitSearch = () => {
    const trimmed = keyword.trim();
    navigate(trimmed ? `/search?q=${encodeURIComponent(trimmed)}` : '/search');
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      submitSearch();
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled || menuOpen
          ? 'bg-background-50/95 backdrop-blur border-b border-background-200'
          : 'bg-background-50/80 backdrop-blur'
      }`}
    >
      <div className="flex items-center gap-4 h-16 px-4 md:px-6">
        <Link to="/" className="flex items-center gap-2 shrink-0">
          <span className="w-9 h-9 flex items-center justify-center rounded-md bg-primary-500 text-background-50">
            <i className="ri-play-circle-fill text-xl"></i>
          </span>
          <span className="font-heading font-bold text-lg text-foreground-950 tracking-tight whitespace-nowrap">
            高评分电视剧
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-1 flex-1">
          <Link
            to="/"
            className="px-3 py-2 rounded-md text-sm font-medium text-foreground-700 hover:text-primary-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
          >
            首页
          </Link>
          {navCategories.map((category) => (
            <Link
              key={category.slug}
              to={`/category/${category.slug}`}
              className="px-3 py-2 rounded-md text-sm font-medium text-foreground-700 hover:text-primary-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              {category.name}
            </Link>
          ))}
          <Link
            to="/about"
            className="px-3 py-2 rounded-md text-sm font-medium text-foreground-700 hover:text-primary-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
          >
            关于我们
          </Link>
        </nav>

        <div className="hidden md:flex items-center gap-2 ml-auto">
          <div className="flex items-center gap-2 h-10 px-3 rounded-full bg-background-100 border border-background-200 focus-within:border-primary-400 transition-colors">
            <i className="ri-search-line text-foreground-400 text-base"></i>
            <input
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              onKeyDown={onKeyDown}
              placeholder="搜索剧名、类型或主演"
              className="w-40 xl:w-56 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 outline-none"
              aria-label="搜索电视剧"
            />
            <button
              type="button"
              onClick={submitSearch}
              className="text-sm font-medium text-primary-600 hover:text-primary-700 cursor-pointer whitespace-nowrap"
            >
              搜索
            </button>
          </div>
        </div>

        <button
          type="button"
          onClick={() => setMenuOpen((prev) => !prev)}
          aria-label="打开导航菜单"
          className="ml-auto lg:hidden w-10 h-10 flex items-center justify-center rounded-md border border-background-200 text-foreground-800 cursor-pointer"
        >
          <i className={menuOpen ? 'ri-close-line text-xl' : 'ri-menu-line text-xl'}></i>
        </button>
      </div>

      {menuOpen && (
        <div className="lg:hidden border-t border-background-200 bg-background-50 px-4 pb-4 pt-3 animate-fade-in">
          <div className="flex items-center gap-2 h-10 px-3 mb-3 rounded-full bg-background-100 border border-background-200 focus-within:border-primary-400">
            <i className="ri-search-line text-foreground-400 text-base"></i>
            <input
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              onKeyDown={onKeyDown}
              placeholder="搜索剧名、类型或主演"
              className="flex-1 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 outline-none"
              aria-label="搜索电视剧"
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Link
              to="/"
              className="px-3 py-2.5 rounded-md bg-background-100 text-sm font-medium text-foreground-800 cursor-pointer"
            >
              首页
            </Link>
            {navCategories.map((category) => (
              <Link
                key={category.slug}
                to={`/category/${category.slug}`}
                className="px-3 py-2.5 rounded-md bg-background-100 text-sm font-medium text-foreground-800 cursor-pointer"
              >
                {category.name}
              </Link>
            ))}
            <Link
              to="/about"
              className="px-3 py-2.5 rounded-md bg-primary-500 text-sm font-medium text-background-50 cursor-pointer"
            >
              关于我们
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}